import Compressor from "compressorjs";
import { drawFitPreview, loadImageElement } from "./cropperr/crop-math";

/**
 * Preparação das fotos de imóveis no browser (imóveis e empreendimentos).
 *
 * O browser só reduz e envia: o servidor guarda um master (≤ 2000 px) e
 * deriva a publicada 16:9. Por isso o limite de tamanho é verificado
 * **depois** da redução — uma foto de 18 MB de câmara fica com 1 a 2 MB.
 */

/** Teto antes de descodificar (proteção de memória do browser). */
export const IMAGE_UPLOAD_MAX_ORIGINAL_BYTES = 40 * 1024 * 1024;
export const IMAGE_UPLOAD_MAX_ORIGINAL_MB = 40;

/** Teto do ficheiro que vai para o servidor, já reduzido. */
export const IMAGE_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
export const IMAGE_UPLOAD_MAX_MB = 15;

/** Lado maior do ficheiro enviado; é também o lado maior do master no servidor. */
export const IMAGE_UPLOAD_MAX_SIDE = 2000;

/** Miniatura do card da galeria. */
export const IMAGE_THUMB_SIZE = 500;

export const IMAGE_UPLOAD_MAX_COUNT = 50;

export const IMAGE_UPLOAD_VALID_TYPES = ["jpg", "jpeg", "png", "webp"];

export interface UploadProgress {
  current: number;
  total: number;
  name: string;
}

export function fileTypeExtension(type: string | undefined | null): string {
  return `${type ?? ""}`.split("/")[1] ?? "";
}

export function isValidImageType(file: File): boolean {
  return IMAGE_UPLOAD_VALID_TYPES.includes(fileTypeExtension(file?.type));
}

/**
 * Dimensões em píxeis sem descodificar para canvas: o browser lê só o
 * cabeçalho. O object URL é revogado logo a seguir.
 */
export function probeImageDimensions(
  file: File | Blob,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler a imagem"));
    };
    img.src = url;
  });
}

/** Redimensiona com o Compressor.js (mantém proporção e aplica EXIF). */
export function compressImage(
  file: File | Blob,
  maxSize: number,
  quality?: number,
  strict = true,
): Promise<File> {
  const name = file instanceof File ? file.name : "image.jpg";
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      maxWidth: maxSize,
      maxHeight: maxSize,
      quality,
      strict,
      success: (result: File | Blob) =>
        resolve(
          result instanceof File
            ? result
            : new File([result], name, { type: result.type }),
        ),
      error: (error: Error) => reject(error),
    });
  });
}

/**
 * Ficheiro a enviar: reduzido a 2000 px quando o original é maior; abaixo
 * disso segue intacto (sem recodificar — é nas fotos pequenas que uma
 * geração extra de perda mais se nota). Em caso de erro devolve o original.
 */
export async function prepareUploadFile(
  file: File,
  dimensions: { width: number; height: number } | null,
): Promise<File> {
  const longest = Math.max(dimensions?.width ?? 0, dimensions?.height ?? 0);
  if (longest > 0 && longest <= IMAGE_UPLOAD_MAX_SIDE) return file;
  try {
    return await compressImage(file, IMAGE_UPLOAD_MAX_SIDE, 0.9, false);
  } catch (error: any) {
    console.error("Erro ao reduzir a imagem:", error?.message ?? error);
    return file;
  }
}

/**
 * Miniatura do card de uma imagem que o automático publica inteira (tela
 * 16:9 com fundo desfocado), para mostrar o que o servidor vai publicar. Em caso
 * de erro devolve a miniatura recebida.
 */
export async function renderFitThumbnail(thumb: Blob): Promise<Blob> {
  try {
    const img = await loadImageElement(thumb);
    const canvas = drawFitPreview(img, img.naturalWidth, img.naturalHeight, "blur", IMAGE_THUMB_SIZE);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((result) => resolve(result), "image/jpeg", 0.85),
    );
    return blob ?? thumb;
  } catch (error: any) {
    console.error("Erro ao gerar a miniatura inteira:", error?.message ?? error);
    return thumb;
  }
}
